/** @jsxImportSource @emotion/react */

import { Link, Tv } from 'lucide-react';
import { Board } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { Game } from 'modules/games/domain/interfaces/game';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { fetchTemplateWithBoards } from 'modules/template-editor/store/templates/actions';
import { selectBoardsByTemplateId, selectTemplateById } from 'modules/template-editor/store/templates/selectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { SyncLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { AppDispatch, history } from 'store/store';
import EmptyButton from 'ud-ui/components/empty-button';
import BoardItem from 'modules/template-editor/ui/components/view-boards/boards-list/board-item';
import BoardsList from 'modules/template-editor/ui/components/view-boards/boards-list/boards-list';
import UdHeaderViewBoards from 'modules/template-editor/ui/components/view-boards/right/ud-header-view-boards';
import { Container, LeftContainer, RightContainer } from 'modules/template-editor/ui/components/view-boards/styles';
import UDText from 'ud-ui/components/text';
import UDTippy from 'ud-ui/components/tippy';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';
import { ChampionshipSettings } from 'modules/championship/domain/interfaces/championship-settings';
import { TeamByMatchId } from 'modules/championship/domain/interfaces/team-by-match-id';
import championshipGameRepository from 'modules/championship/domain/repositories/championship-game.repository';
import OverlayCanvas from './overlay-canvas';
import { theme } from 'styles/theme/theme';

type MatchOverlayPreviewProps = {
  game: Game;
  championship: {
    id: number;
    settings: ChampionshipSettings;
    tournamentId: number;
    address?: {
      city?: string;
    };
    name: string;
  };
};

function MatchOverlayPreview({ game, championship }: MatchOverlayPreviewProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoaded, isLoading } = useTemplates();
  const template = useSelector(selectTemplateById(championship?.settings?.overlayId ?? undefined));
  const boards = useSelector(selectBoardsByTemplateId(template?.id), shallowEqual);
  const [activeBoard, setActiveBoard] = useState<Board>();
  const [teamsPlayers, setTeamsPlayers] = useState<TeamByMatchId[]>();
  const isAdmin = useIsAdmin();

  useEffect(() => {
    championshipGameRepository.fetchTeamPlayersByMatchId(game.id).then((teams) => {
      if (!teams) return;
      if (teams[0].teamId === game.teams[0]?.id && teams[1].teamId === game.teams[1]?.id) {
        setTeamsPlayers(teams);
      } else if (teams[0].teamId === game.teams[1]?.id && teams[1].teamId === game.teams[0]?.id) {
        setTeamsPlayers(teams.reverse());
      }
    });
  }, [game.id, game.teams]);

  useEffect(() => {
    if (isLoaded && boards.length > 0) return;
    if (!championship.settings.overlayId) return;
    dispatch(fetchTemplateWithBoards({ templateId: championship.settings.overlayId }));
  }, [boards.length, championship.settings.overlayId, championship.tournamentId, dispatch, isAdmin, isLoaded]);

  useEffect(() => {
    if (!template) return;
    if (boards.length <= 0) return;
    setActiveBoard(boards[0]);
  }, [boards, template]);

  const handleCreateLink = useCallback(
    (data?: { boardId?: number; isLocked?: boolean; isHidePanel?: boolean; fullScreenPanel?: boolean }) => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Необходимо авторизоваться');
        return;
      }

      const { boardId, isLocked = false, isHidePanel = false, fullScreenPanel = false } = data ?? {};

      const encodedToken = window.btoa(token);
      const domain = fullScreenPanel ? 'control-panel' : 'cover';
      navigator.clipboard
        .writeText(
          `${window.location.origin}/${domain}/${game.id}/?token=${encodedToken}${
            data ? `&boardId=${boardId}&locked=${isLocked}&hidePanel=${isHidePanel}` : ''
          }`,
        )
        .then(() => {
          toast.success('Ссылка скопирована');
        })
        .catch(() => {
          toast.error('Не удалось скопировать ссылку');
        });
    },
    [game.id],
  );

  const ItemList = useMemo(() => {
    if (!boards) return undefined;

    return boards.map((board) => {
      const button =
        board.type === 'tv' ? (
          <UDTippy content={'ссылка на панель управления TV'} placement={'top'}>
            <EmptyButton
              icon={<Link size={16} />}
              onClick={() =>
                handleCreateLink({
                  boardId: board.id,
                  fullScreenPanel: true,
                })
              }
            />
          </UDTippy>
        ) : null;

      return (
        <BoardItem
          key={board.id}
          board={board}
          isEditable={false}
          onClick={() => setActiveBoard(board)}
          isActive={board.id === activeBoard?.id}
          additionalButton={button}
          icon={board.type === 'tv' && <Tv size={16} />}
        />
      );
    });
  }, [activeBoard?.id, boards, handleCreateLink]);

  const onClickGetLinkOverlay = () => {
    handleCreateLink();
  };

  const redirectToSettings = useCallback(() => {
    history.push(Pages.CHAMPIONSHIPS.builders.edit(championship.id, 'common'), { highlight: ['template'] });
  }, [championship.id]);

  return (
    <Container className="h-full px-6 pb-5" style={{ height: '100%', minHeight: '100%' }}>
      {isLoading || !teamsPlayers ? (
        <div className={'flex-center w-full'} style={{ height: 450 }}>
          <SyncLoader />
        </div>
      ) : template === undefined ? (
        <div className="flex-center w-full flex-col" style={{ height: 400 }}>
          <UDText type={'headline'}>Не выбран шаблон</UDText>
          <UDText type={'subhead'} style={{ color: theme.colors.EditorAccentBlue, cursor: 'pointer' }} onClick={redirectToSettings}>
            Выберите шаблон из списка
          </UDText>
        </div>
      ) : (
        <>
          <LeftContainer>
            <BoardsList items={ItemList ?? []} headerText={'Табло'} />
          </LeftContainer>
          <RightContainer>
            <UdHeaderViewBoards
              mainText={activeBoard?.name}
              buttons={[
                <EmptyButton onClick={onClickGetLinkOverlay} icon={<Link size={16} />}>
                  Скопировать ссылку
                </EmptyButton>,
              ]}
            />
            <OverlayCanvas
              game={game}
              teamsPlayers={teamsPlayers}
              championship={championship}
              resolution={template?.resolution}
              scheme={activeBoard ? { version: activeBoard.version, objects: activeBoard.objects } : undefined}
            />
          </RightContainer>
        </>
      )}
    </Container>
  );
}

export default MatchOverlayPreview;
